const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
};

// Generic API request handler
export const apiRequest = async (endpoint, options = {}) => {
  const url = `${BACKEND_URL}${endpoint}`;
  const config = {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
      throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// Customer API
export const customerAPI = {
  getAll: () => apiRequest('/api/customers'),
  
  create: (data) => apiRequest('/api/customers', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiRequest(`/api/customers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiRequest(`/api/customers/${id}`, {
    method: 'DELETE',
  }),
};

// Auth API
export const authAPI = {
  login: (username, password) => 
    fetch(`${BACKEND_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    }).then(res => res.json()),
  
  getMe: () => apiRequest('/api/auth/me'),
  
  register: (data) => apiRequest('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// User Management API
export const userAPI = {
  getAll: () => apiRequest('/api/users'),
  
  create: (data) => apiRequest('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiRequest(`/api/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  changePassword: (id, newPassword) => apiRequest(`/api/users/${id}/password`, {
    method: 'PUT',
    body: JSON.stringify({ new_password: newPassword }),
  }),
  
  delete: (id) => apiRequest(`/api/users/${id}`, {
    method: 'DELETE',
  }),
};

// Employee API
export const employeeAPI = {
  getAll: () => apiRequest('/api/employees'),
  
  create: (data) => apiRequest('/api/employees', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiRequest(`/api/employees/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiRequest(`/api/employees/${id}`, {
    method: 'DELETE',
  }),
};

// Attendance API
export const attendanceAPI = {
  getAll: (date = null) => {
    const url = date ? `/api/attendance?date=${date}` : '/api/attendance';
    return apiRequest(url);
  },
  
  create: (data) => apiRequest('/api/attendance', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiRequest(`/api/attendance/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiRequest(`/api/attendance/${id}`, {
    method: 'DELETE',
  }),
};

// Order API
export const orderAPI = {
  getAll: () => apiRequest('/api/orders'),
  
  create: (data) => apiRequest('/api/orders', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiRequest(`/api/orders/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiRequest(`/api/orders/${id}`, {
    method: 'DELETE',
  }),
};

// Todo API
export const todoAPI = {
  getAll: () => apiRequest('/api/todos'),
  
  create: (data) => apiRequest('/api/todos', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiRequest(`/api/todos/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiRequest(`/api/todos/${id}`, {
    method: 'DELETE',
  }),
};

// Remainder/Maintenance Schedule API
export const remainderAPI = {
  getAll: () => apiRequest('/api/remainders'),
  
  getPending: () => apiRequest('/api/remainders/pending'),
  
  create: (data) => apiRequest('/api/remainders', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiRequest(`/api/remainders/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  complete: (id) => apiRequest(`/api/remainders/${id}/complete`, {
    method: 'PUT',
  }),
  
  undoComplete: (id, previousDueDate) => apiRequest(`/api/remainders/${id}/undo-complete?previous_due_date=${previousDueDate}`, {
    method: 'PUT',
  }),
  
  delete: (id) => apiRequest(`/api/remainders/${id}`, {
    method: 'DELETE',
  }),
};

// Checklist API
export const checklistAPI = {
  getAll: (type = null, date = null) => {
    let url = '/api/checklists';
    const params = [];
    if (type) params.push(`checklist_type=${type}`);
    if (date) params.push(`date=${date}`);
    if (params.length > 0) url += `?${params.join('&')}`;
    return apiRequest(url);
  },
  
  create: (data) => apiRequest('/api/checklists', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiRequest(`/api/checklists/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiRequest(`/api/checklists/${id}`, {
    method: 'DELETE',
  }),
};

// Stock API
export const stockAPI = {
  getAll: () => apiRequest('/api/stocks'),
  
  create: (data) => apiRequest('/api/stocks', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiRequest(`/api/stocks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiRequest(`/api/stocks/${id}`, {
    method: 'DELETE',
  }),
  
  getTodayReminders: () => apiRequest('/api/stocks/reminders/today'),
};

// Loss Cal API
export const lossCalAPI = {
  getAll: () => apiRequest('/api/loss-cal'),
  
  create: (data) => apiRequest('/api/loss-cal', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiRequest(`/api/loss-cal/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiRequest(`/api/loss-cal/${id}`, {
    method: 'DELETE',
  }),
};

// Estimate API
export const estimateAPI = {
  getAll: () => apiRequest('/api/estimates'),
  
  create: (data) => apiRequest('/api/estimates', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiRequest(`/api/estimates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiRequest(`/api/estimates/${id}`, {
    method: 'DELETE',
  }),
  
  deleteAll: () => apiRequest('/api/estimates/all', {
    method: 'DELETE',
  }),

  // Defaults management
  getDefaults: () => apiRequest('/api/estimate-defaults'),
  
  saveDefaults: (data) => apiRequest('/api/estimate-defaults', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Defaults (Images) API
export const defaultsAPI = {
  getAll: () => apiRequest('/api/defaults-images'),
  
  upload: (formData) => fetch(`${BACKEND_URL}/api/defaults-images`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
    },
    body: formData, // FormData for file upload
  }).then(res => res.json()),
  
  delete: (id) => apiRequest(`/api/defaults-images/${id}`, {
    method: 'DELETE',
  }),
};

// Address Book API
export const addressBookAPI = {
  getAll: (search = null) => {
    const url = search ? `/api/addresses?search=${encodeURIComponent(search)}` : '/api/addresses';
    return apiRequest(url);
  },
  
  create: (data) => apiRequest('/api/addresses', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiRequest(`/api/addresses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiRequest(`/api/addresses/${id}`, {
    method: 'DELETE',
  }),
  
  getDefaultAddress: () => apiRequest('/api/addresses/settings/default'),
  
  setDefaultAddress: (id) => apiRequest(`/api/addresses/settings/default?address_id=${id}`, {
    method: 'POST',
  }),
};

// Activity Log API
export const activityLogAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.entity_type) params.append('entity_type', filters.entity_type);
    if (filters.username) params.append('username', filters.username);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.limit) params.append('limit', filters.limit);
    
    const url = params.toString() ? `/api/activity-logs?${params.toString()}` : '/api/activity-logs';
    return apiRequest(url);
  },
};

// CAD Orders API
export const cadOrderAPI = {
  getAll: () => apiRequest('/api/cad-orders'),
  
  create: (data) => apiRequest('/api/cad-orders', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiRequest(`/api/cad-orders/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiRequest(`/api/cad-orders/${id}`, {
    method: 'DELETE',
  }),
};

// App Settings API
export const settingsAPI = {
  get: () => apiRequest('/api/settings'),
  
  update: (data) => apiRequest('/api/settings', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

export const externalLinksAPI = {
  list: () => apiRequest('/api/external-links'),
  
  create: (data) => apiRequest('/api/external-links', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiRequest(`/api/external-links/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiRequest(`/api/external-links/${id}`, {
    method: 'DELETE',
  }),
};
